import { useEffect, useMemo, useState } from 'react';
import { BsChat } from 'react-icons/bs';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import config from '@/config';
import useCache from '@/hooks/useCache';
import useGoBack from '@/hooks/useGoBack';
import useStateHandler from '@/hooks/useStateHandler';
import ArtworkImage from '@/shared-components/cards/components/ArtworkImage';
import { useDropdownPopover } from '@/shared-components/dropdown';
import BackArrowIcon from '@/shared-components/icons/components/public/BackArrowIcon';
import ThreeDotsHorizontal from '@/shared-components/icons/components/public/styles/ThreeDotsHorizontal';
import Loader from '@/shared-components/loader/Loader'; // ✅ Updated import

import CancelOrderModal from './components/CancelOrderModal';
import OrderInfo from './components/OrderInfo';
import VariableContent from './components/VariableContent';
import Conversation from './conversation';
import IssueDetails from './issue-details';

import './index.css';

export default function OrderDetails() {
  const { orderNumber } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { loading, fetchData, setFetchData, refetch } = useCache(
    `order_details_of_${orderNumber}`,
    `${config.apis.api.url}/order/${orderNumber}`,
    {
      injectToken: true,
      expiresIn: ['0', 'seconds'],
      invalidateWhen: ['ORDER_MESSAGES_READ'],
    }
  );
  const { state } = useStateHandler();
  const [openIssueDetails, setOpenIssueDetails] = useState(() => Boolean(location.state?.openIssueDetails));
  const conversationRequested = new URLSearchParams(location.search).get('conversation') === '1';
  const [openConversation, setOpenConversation] = useState(conversationRequested);
  const [showShippingForm, setShowShippingForm] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [showCancelMenu, setShowCancelMenu] = useState(false);
  const { rootRef: cancelMenuRef, triggerRef: cancelMenuTriggerRef, menuId: cancelMenuId, close: closeCancelMenu, toggle: toggleCancelMenu, handleTriggerKeyDown: handleCancelMenuTriggerKeyDown } = useDropdownPopover({
    open: showCancelMenu,
    onOpenChange: setShowCancelMenu,
    idPrefix: 'order-cancel-actions',
  });
  const goBackHandler = useGoBack('/profile');
  const currentUserId = state?.user_session?._id ?? null;
  const orderBuyerId = fetchData?.order_buyer?._id ?? fetchData?.order_buyer ?? null;
  const viewerIsBuyer = Boolean(
    currentUserId &&
    orderBuyerId &&
    String(currentUserId) === String(orderBuyerId)
  );
  const captureWindowMs = useMemo(() => {
    const candidateDates = [
      fetchData?.order_payment?.capture_due_at,
      fetchData?.capture_due_at,
      fetchData?.authorized_until,
    ];
    const parsedCandidate = candidateDates
      .map((entry) => Date.parse(entry))
      .find((value) => Number.isFinite(value));
    if (Number.isFinite(parsedCandidate)) {
      return parsedCandidate;
    }
    const authorizedAt = [
      fetchData?.order_payment?.authorized_at,
      fetchData?.authorized_at,
      fetchData?.order_payment?.createdAt,
      fetchData?.created_at,
      fetchData?.createdAt,
    ]
      .map((entry) => Date.parse(entry))
      .find((value) => Number.isFinite(value));
    if (Number.isFinite(authorizedAt)) {
      return authorizedAt + 24 * 60 * 60 * 1000;
    }
    return null;
  }, [fetchData]);
  const paymentStatus = useMemo(() => {
    const rawStatus = (
      fetchData?.payment_status
      ?? fetchData?.order_payment?.payment_status
      ?? fetchData?.order_payment?.status
      ?? fetchData?.status
      ?? fetchData?.order_status
      ?? ""
    );
    return typeof rawStatus === "string" ? rawStatus.toLowerCase() : "";
  }, [fetchData]);
  const isCancelled = paymentStatus.includes("cancel");
  const isCaptured = ["paid", "succeeded", "captured", "completed", "sent", "delivered"]
    .some((status) => paymentStatus === status);
  const withinCancellationWindow = useMemo(() => {
    if (!captureWindowMs) {
      return false;
    }
    return Date.now() < captureWindowMs;
  }, [captureWindowMs]);
  const windowAllowsCancel = withinCancellationWindow || (captureWindowMs === null && paymentStatus === "authorized");
  const canCancelOrder = viewerIsBuyer && windowAllowsCancel && !isCancelled && !isCaptured;
  const handleCancelled = (updatedOrder) => {
    if (typeof setFetchData === "function") {
      setFetchData((prev) => ({
        ...(prev ?? {}),
        ...(updatedOrder ?? {}),
        status: updatedOrder?.status ?? updatedOrder?.order_status ?? "cancelled",
        order_status: updatedOrder?.order_status ?? updatedOrder?.status ?? "cancelled",
      }));
    }
  };

  useEffect(() => {
    if (cancelModalOpen) {
      setShowCancelMenu(false);
    }
  }, [cancelModalOpen]);

  useEffect(() => {
    setOpenConversation(conversationRequested);
  }, [conversationRequested, orderNumber]);

  const openCancelModal = () => {
    setCancelModalOpen(true);
    closeCancelMenu();
  };

  const openConversationHandler = () => {
    navigate(`/order/${orderNumber}?conversation=1`, { state: location.state });
  };

  const closeConversationHandler = () => {
    navigate(`/order/${orderNumber}`, { replace: true, state: location.state });
  };
  const unreadMessageCount = Number(fetchData?.unreadMessageCount) || 0;

  return (
    <>
      <div
        className={`order-details__page ${
          openIssueDetails || openConversation ? 'remove-header' : ''
        }`}
      >
        <header className="order-details__header">
          <BackArrowIcon className="order-details__back-button" onClick={goBackHandler} />
          <span className="order-details__header-text">
            Order #{orderNumber}
          </span>
          {canCancelOrder ? (
            <div
              className="order-details__header-actions"
              ref={(node) => {
                cancelMenuRef.current = node;
                cancelMenuTriggerRef.current = node;
              }}
              tabIndex={0}
              role="button"
              onKeyDown={handleCancelMenuTriggerKeyDown}
              aria-haspopup="menu"
              aria-expanded={showCancelMenu}
              aria-controls={cancelMenuId}
            >
              <ThreeDotsHorizontal
                className="order-details__header-dots artwork-options__fake-dots"
                onClick={toggleCancelMenu}
              />
              {showCancelMenu && (
                <div id={cancelMenuId} className="order-details__cancel-menu header" role="menu">
                  <button
                    type="button"
                    className="order-details__cancel-menu-item"
                    onClick={openCancelModal}
                  >
                    Cancel order
                  </button>
                </div>
              )}
            </div>
          ) : null}
        </header>

        {loading ? (
          <Loader />
        ) : fetchData?.error_type === 'NOT_FOUND' ? (
          <div className="order-details__not-found-page">
            <span className="order-details__not-found-oops">Oops!</span>
            <span className="order-details__empty-text">
              We couldn't find an order for the order number you entered. If you
              believe this is a mistake, please contact our support team for assistance.
            </span>
          </div>
        ) : (
          <main className="order-details__main">
            <ArtworkImage
              image={fetchData?.order_artwork?.artwork_media?.artwork_main_picture?.image_id}
              imageClassName="order-details__image"
              imageTemplateClassName="order-details__image"
            />

            <div className="order-details__section">
              {openConversation ? (
                <Conversation close={closeConversationHandler} orderData={fetchData} />
              ) : openIssueDetails ? (
                <IssueDetails
                  close={() => setOpenIssueDetails(false)}
                  orderData={fetchData}
                  setOrderData={setFetchData}
                />
              ) : (
                <>
                  <OrderInfo
                    fetchData={fetchData}
                    setOpenIssueDetails={setOpenIssueDetails}
                    onAddShippingAddress={() => setShowShippingForm(true)}
                  />

                  {!fetchData?.order_limited_edition_data && (
                    <div
                      className={`order-details__open-conversation-container element-non-selectable${unreadMessageCount > 0 ? ' order-details__open-conversation-container--unread' : ''}`}
                      onClick={openConversationHandler}
                    >
                      <BsChat className="order-details__conversation-icon" />
                      <span className="order-details__conversation-text">
                        Chat with{' '}
                        <span className="order-details__conversation-recipient">
                          {viewerIsBuyer
                            ? fetchData?.order_seller?.user_name ?? "Gallery"
                            : fetchData?.order_buyer?.user_username ?? orderBuyerId ?? "Collector"}
                        </span>
                      </span>
                      {unreadMessageCount > 0 ? (
                        <span className="order-details__conversation-unread-count" aria-label={`${unreadMessageCount} new messages`}>
                          {unreadMessageCount}
                        </span>
                      ) : null}
                    </div>
                  )}

                  <VariableContent
                    fetchData={fetchData}
                    setOrderData={setFetchData}
                    showShippingForm={showShippingForm}
                    setShowShippingForm={setShowShippingForm}
                    refetchOrder={refetch}
                  />
                </>
              )}
            </div>
          </main>
        )}
      </div>

      <CancelOrderModal
        open={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        orderNumber={orderNumber}
        onCancelled={handleCancelled}
      />
    </>
  );
}
